import { useState } from 'react';
import { t } from '@web/i18n';
import { Button, PageSectionHead, Scaffold } from '@web/ui/core';
import { twcn } from '@web/ui/core/utils';
import { CorporateStep, EventStep } from '../components/new_event_steps';
import { NewEventSummary } from '../components/new_event_summary';
import { useMiceProjectForm } from '../hooks/use_mice_project_form';

type Step = 1 | 2;

const STEPS = [
  { step: 1, label: 'mice.form.step.event', sub: 'mice.form.step.eventSub' },
  { step: 2, label: 'mice.form.step.corporate', sub: 'mice.form.step.corporateSub' },
] as const;

function Stepper(props: {
  current: Step;
  canAdvance: boolean;
  onGo: (step: Step) => void;
}): React.JSX.Element {
  return (
    <nav aria-label={t('mice.form.steps')} className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {STEPS.map(({ step, label, sub }) => {
        const on = step === props.current;
        const done = step < props.current;
        return (
          <button
            key={step}
            type="button"
            aria-current={on ? 'step' : undefined}
            disabled={step === 2 && !props.canAdvance}
            onClick={() => {
              props.onGo(step);
            }}
            className={twcn(
              'flex items-center gap-3 rounded-lg border bg-card p-3 text-left disabled:opacity-60',
              on ? 'border-primary shadow-ring-primary' : 'border-border',
            )}
          >
            <span
              className={twcn(
                'inline-flex h-6 w-6 flex-none items-center justify-center rounded-pill text-meta font-semibold',
                on || done
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground',
              )}
            >
              {step}
            </span>
            <span className="flex min-w-0 flex-col">
              <span className="text-meta font-medium text-foreground">{t(label)}</span>
              <span className="truncate text-micro text-muted-foreground">{t(sub)}</span>
            </span>
          </button>
        );
      })}
    </nav>
  );
}

export function MiceNewProjectPage(): React.JSX.Element {
  const form = useMiceProjectForm();
  const [step, setStep] = useState<Step>(1);

  return (
    <Scaffold
      body={
        <div className="mx-auto flex w-full max-w-reading-w flex-col gap-6">
          <PageSectionHead title={t('mice.form.title')} sub={t('mice.form.sub')} />
          <Stepper current={step} canAdvance={form.eventComplete} onGo={setStep} />
          <div className="flex flex-wrap items-start gap-5">
            <div className="flex min-w-0 flex-1 basis-96 flex-col gap-4">
              {step === 1 ? <EventStep form={form} /> : <CorporateStep form={form} />}
              <div className="flex justify-between gap-2">
                {step === 2 ? (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setStep(1);
                    }}
                  >
                    {t('mice.form.back')}
                  </Button>
                ) : (
                  <span />
                )}
                {step === 1 ? (
                  <Button
                    variant="primary"
                    disabled={!form.eventComplete}
                    onClick={() => {
                      setStep(2);
                    }}
                  >
                    {t('mice.form.continue')}
                  </Button>
                ) : (
                  <Button variant="primary" onClick={form.submit} disabled={!form.canSubmit}>
                    {t('mice.form.submit')}
                  </Button>
                )}
              </div>
            </div>
            <NewEventSummary form={form} />
          </div>
        </div>
      }
    />
  );
}
